// ===========================================
// Reglements — Page liste refondue (UX cohérent avec clients/contrats)
// KPIs + filtres inline + tabs + table enrichie + dropdown actions
// ===========================================
import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
    Table, Card, Input, Select, Button, Tag, DatePicker, Grid, message,
    Modal, Dropdown, Progress, Tabs, Empty, List, Pagination, Spin,
} from 'antd';
import {
    SearchOutlined, PlusOutlined, DollarOutlined, DownloadOutlined,
    FilePdfOutlined, FileExcelOutlined, EyeOutlined,
    BankOutlined, WalletOutlined, CalendarOutlined, FileProtectOutlined,
    UserOutlined, CheckCircleOutlined, ExclamationCircleOutlined, ReloadOutlined,
    TeamOutlined, MoreOutlined, ClearOutlined, PhoneOutlined,
    QuestionCircleOutlined, CloseOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { reglementsService, agencesService } from '../../common/services';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import logoCap from '../../assets/logo/logo-cap-assurances.JPG';
import { formatMontant, formatPhone as formatPhoneShared } from '../../common/utils/format';
import { useReferentiel, useGlobalAgenceState, useCanSwitchAgence, useExporterName } from '../../common/hooks';
import { addExporterFooter, PDF_HEAD_STYLES, PDF_FOOT_STYLES } from '../../common/utils/pdfExport';
import { borderExcelTable, addExcelTotals, addExcelLogoHeader, addExcelExportInfo } from '../../common/utils/excelExport';
import MobileFab from '../../components/mobileFab';
import { useHasPermission } from '../../components/can';

import MiniKpiCard from '../clients/components/MiniKpiCard';

import './reglements.page.scss';

const { Option } = Select;
const { RangePicker } = DatePicker;

// Libellés + couleurs des moyens de paiement
const MODE_PAIEMENT = {
    especes: { label: 'Espèces', color: 'green' },
    cheque: { label: 'Chèque', color: 'blue' },
    virement: { label: 'Virement', color: 'purple' },
    mobile_money: { label: 'Mobile Money', color: 'orange' },
};

const formatPhone = (tel) => formatPhoneShared(tel, { fallback: '' });

const Reglements = () => {
    const navigate = useNavigate();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;

    // ─── Permissions (masquer les actions non autorisées) ─────────────
    const canCreateReglement = useHasPermission('reglements:create');

    // ─── État ─────────────────────────────────────────────────────────
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({});
    const [countByType, setCountByType] = useState({
        particulier: { count: 0, totalPrimes: 0, totalVerse: 0 },
        entreprise: { count: 0, totalPrimes: 0, totalVerse: 0 },
    });
    const [totalAll, setTotalAll] = useState(0);
    const [activeTab, setActiveTab] = useState('all');
    const [search, setSearch] = useState('');
    const [filterAgence, setFilterAgence] = useGlobalAgenceState();
    const canSwitchAgence = useCanSwitchAgence();
    const exporterName = useExporterName();
    const [filterDates, setFilterDates] = useState(null);
    const { data: agences } = useReferentiel('agences', () => agencesService.getAll());
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

    const [guideOpen, setGuideOpen] = useState(false);

    // ─── Fetch ────────────────────────────────────────────────────────
    const fetchData = async (page = 1, pageSize = 20) => {
        setLoading(true);
        try {
            const params = { page, limit: pageSize };
            if (filterAgence !== 'all') params.agence = filterAgence;
            if (search) params.q = search;
            if (activeTab !== 'all') params.clientType = activeTab;
            if (filterDates && filterDates[0] && filterDates[1]) {
                params.start = filterDates[0].format('YYYY-MM-DD');
                params.end = filterDates[1].format('YYYY-MM-DD');
            }
            const res = await reglementsService.etatParClient(params);
            const d = res?.data || res;
            setData(d?.items || []);
            setStats(d?.stats || {});
            setCountByType(d?.countByType || { particulier: { count: 0, totalPrimes: 0, totalVerse: 0 }, entreprise: { count: 0, totalPrimes: 0, totalVerse: 0 } });
            setTotalAll(d?.totalAll || 0);
            setPagination({ current: d?.page || page, pageSize, total: d?.total || 0 });
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, filterAgence, filterDates]);

    useEffect(() => {
        const timer = setTimeout(() => fetchData(), 400);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    // ─── Compteur filtres actifs ──────────────────────────────────────
    const activeFiltersCount = useMemo(() => {
        let n = 0;
        if (filterAgence !== 'all') n++;
        if (filterDates) n++;
        return n;
    }, [filterAgence, filterDates]);

    const clearAllFilters = () => {
        setSearch('');
        setFilterAgence('all');
        setFilterDates(null);
    };

    // ─── Exports ──────────────────────────────────────────────────────
    const fetchAllForExport = async () => {
        const hide = message.loading("Préparation de l'export...", 0);
        try {
            const params = { page: 1, limit: 99999 };
            if (filterAgence !== 'all') params.agence = filterAgence;
            if (search) params.q = search;
            if (activeTab !== 'all') params.clientType = activeTab;
            if (filterDates && filterDates[0] && filterDates[1]) {
                params.start = filterDates[0].format('YYYY-MM-DD');
                params.end = filterDates[1].format('YYYY-MM-DD');
            }
            const res = await reglementsService.etatParClient(params);
            const d = res?.data || res;
            hide();
            return d?.items || [];
        } catch (e) {
            hide();
            toast.error("Erreur lors de la préparation de l'export");
            return [];
        }
    };

    const handleExportPDF = async () => {
        const exportRows = await fetchAllForExport();
        if (!exportRows.length) return;
        const doc = new jsPDF({ orientation: 'landscape' });
        const today = new Date().toLocaleDateString('fr-FR');
        const pageWidth = doc.internal.pageSize.width;
        const centerX = pageWidth / 2;
        const fmtPdf = (v) => formatMontant(v).replace(/\s/g, ' ');
        try { doc.addImage(logoCap, 'JPEG', 14, 8, 40, 16); } catch (e) { /* */ }
        doc.setFontSize(10);
        doc.setTextColor(0);
        doc.text(today, pageWidth - 14, 15, { align: 'right' });
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text('ETAT DES REGLEMENTS PAR ASSURE', centerX, 30, { align: 'center' });
        doc.setFont(undefined, 'normal');

        // Période de filtre (si renseignée)
        let pdfStartY = 36;
        if (filterDates && filterDates[0] && filterDates[1]) {
            const du = filterDates[0].format('DD/MM/YYYY');
            const au = filterDates[1].format('DD/MM/YYYY');
            doc.setFontSize(10);
            doc.setFont(undefined, 'bold');
            const periodeLabel = 'Période du : ';
            const periodeMid = '   au   ';
            const fullPeriode = `${periodeLabel}${du}${periodeMid}${au}`;
            const totalW = doc.getTextWidth(fullPeriode);
            const startX = centerX - totalW / 2;
            doc.setTextColor(0);
            doc.text(periodeLabel, startX, 38);
            doc.setTextColor(139, 0, 0);
            doc.text(du, startX + doc.getTextWidth(periodeLabel), 38);
            doc.setTextColor(0);
            doc.text(periodeMid, startX + doc.getTextWidth(periodeLabel + du), 38);
            doc.setTextColor(139, 0, 0);
            doc.text(au, startX + doc.getTextWidth(periodeLabel + du + periodeMid), 38);
            doc.setTextColor(0);
            doc.setFont(undefined, 'normal');
            pdfStartY = 44;
        }

        // Une ligne PAR CONTRAT ; le nom du client et son téléphone sont fusionnés
        // (rowSpan) sur toutes les lignes de ses contrats.
        let totPrimes = 0, totPaye = 0, totReste = 0;
        const bodyRows = [];
        exportRows.forEach((r) => {
            const contrats = (r.contrats && r.contrats.length)
                ? r.contrats
                : [{ numeroPolice: '—', compagnie: '', primeTTC: r.totalPrimeTTC, verse: r.totalVerse }];
            const nom = `${r.client?.nom || ''} ${r.client?.prenom || ''}`.trim() || '-';
            const tel = formatPhone(r.client?.telephone);
            contrats.forEach((c, i) => {
                const prime = c.primeTTC || 0;
                const verse = c.verse || 0;
                const reste = Math.max(0, prime - verse);
                totPrimes += prime; totPaye += verse; totReste += reste;
                const row = [];
                if (i === 0) {
                    row.push({ content: nom, rowSpan: contrats.length, styles: { valign: 'middle' } });
                    row.push({ content: tel, rowSpan: contrats.length, styles: { valign: 'middle' } });
                }
                row.push(
                    c.numeroPolice || '—',
                    c.compagnie || '—',
                    fmtPdf(prime),
                    fmtPdf(verse),
                    fmtPdf(reste),
                );
                bodyRows.push(row);
            });
        });

        autoTable(doc, {
            startY: pdfStartY,
            head: [['Assurés', 'Téléphone', 'N° Police', 'Compagnie', 'Total primes', 'Payé', 'Reste']],
            body: bodyRows,
            foot: [[
                { content: 'TOTAL', colSpan: 4, styles: { halign: 'right', fontStyle: 'bold' } },
                { content: fmtPdf(totPrimes), styles: { halign: 'right', fontStyle: 'bold' } },
                { content: fmtPdf(totPaye), styles: { halign: 'right', fontStyle: 'bold' } },
                { content: fmtPdf(totReste), styles: { halign: 'right', fontStyle: 'bold' } },
            ]],
            showFoot: 'lastPage',
            styles: { fontSize: 8, cellPadding: 3, lineColor: [0, 0, 0], lineWidth: 0.3, fillColor: false },
            headStyles: PDF_HEAD_STYLES,
            footStyles: PDF_FOOT_STYLES,
            alternateRowStyles: { fillColor: false },
            columnStyles: { 2: { halign: 'center' }, 3: { halign: 'left' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' } },
            margin: { bottom: 35 },
        });
        addExporterFooter(doc, exporterName);
        doc.save(`reglements_par_client_${today.replace(/\//g, '-')}.pdf`);
        toast.success('PDF exporté');
    };

    const handleExportExcel = async () => {
        const exportRows = await fetchAllForExport();
        if (!exportRows.length) return;
        const today = new Date().toLocaleDateString('fr-FR');
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Règlements par client');
        const headers = ['Assurés', 'Téléphone', 'N° Police', 'Compagnie', 'Total primes', 'Payé', 'Reste'];
        await addExcelLogoHeader(wb, ws);
        const titleRow = ws.addRow(['ETAT DES REGLEMENTS PAR ASSURE']);
        ws.mergeCells(titleRow.number, 1, titleRow.number, headers.length);
        titleRow.getCell(1).font = { bold: true, size: 14, color: { argb: '1A2B5F' } };
        titleRow.getCell(1).alignment = { horizontal: 'center' };

        // Période de filtre (si renseignée)
        if (filterDates && filterDates[0] && filterDates[1]) {
            const du = filterDates[0].format('DD/MM/YYYY');
            const au = filterDates[1].format('DD/MM/YYYY');
            const periodeRow = ws.addRow([`Période du ${du} au ${au}`]);
            ws.mergeCells(periodeRow.number, 1, periodeRow.number, headers.length);
            periodeRow.getCell(1).font = { bold: true, italic: true, size: 11, color: { argb: '8B0000' } };
            periodeRow.getCell(1).alignment = { horizontal: 'center' };
        }

        addExcelExportInfo(ws, { colCount: headers.length, exporterName, today });
        ws.addRow([]);
        const headerRow = ws.addRow(headers);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: '000000' }, size: 11 };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
        });
        // Une ligne PAR CONTRAT ; Client + Téléphone fusionnés sur les lignes du client.
        exportRows.forEach((r) => {
            const contrats = (r.contrats && r.contrats.length)
                ? r.contrats
                : [{ numeroPolice: '—', compagnie: '', primeTTC: r.totalPrimeTTC, verse: r.totalVerse }];
            const nom = `${r.client?.nom || ''} ${r.client?.prenom || ''}`.trim() || '-';
            const tel = formatPhone(r.client?.telephone);
            let firstRowNum = null;
            let lastRowNum = null;
            contrats.forEach((c, i) => {
                const prime = c.primeTTC || 0;
                const verse = c.verse || 0;
                const row = ws.addRow([
                    i === 0 ? nom : '',
                    i === 0 ? tel : '',
                    c.numeroPolice || '—',
                    c.compagnie || '—',
                    prime,
                    verse,
                    Math.max(0, prime - verse),
                ]);
                if (firstRowNum === null) firstRowNum = row.number;
                lastRowNum = row.number;
                row.getCell(1).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
                row.getCell(3).alignment = { horizontal: 'center', vertical: 'middle' };
            });
            if (lastRowNum > firstRowNum) {
                ws.mergeCells(firstRowNum, 1, lastRowNum, 1);
                ws.mergeCells(firstRowNum, 2, lastRowNum, 2);
                ws.getCell(firstRowNum, 1).alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
                ws.getCell(firstRowNum, 2).alignment = { horizontal: 'left', vertical: 'middle' };
            }
        });
        ws.columns.forEach((col) => { col.width = 18; });
        const totalRow = addExcelTotals(ws, { headerRow: headerRow.number, numericCols: [4, 5, 6], labelCol: 1 });
        // Fusionne le libellé « TOTAL » sur Client + Téléphone + N° Police + Compagnie (aligné à droite)
        if (totalRow) {
            ws.mergeCells(totalRow.number, 1, totalRow.number, 4);
            totalRow.getCell(1).alignment = { horizontal: 'right' };
        }
        borderExcelTable(ws, headerRow.number, headers.length);
        const buffer = await wb.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `reglements_par_client_${today.replace(/\//g, '-')}.xlsx`);
        toast.success('Excel exporté');
    };

    const exportMenuItems = [
        { key: 'pdf',   label: 'Exporter en PDF',   icon: <FilePdfOutlined style={{ color: '#C8102E' }} />, onClick: handleExportPDF },
        { key: 'excel', label: 'Exporter en Excel', icon: <FileExcelOutlined style={{ color: '#389e0d' }} />, onClick: handleExportExcel },
    ];

    // ─── Menu actions par ligne ───────────────────────────────────────
    const getRowActionsMenu = (record) => ({
        items: [
            {
                key: 'detail',
                icon: <EyeOutlined />,
                label: 'Voir détail',
                onClick: () => navigate(`/dashboard/detail-reglement/${record.client?._id}`),
            },
            canCreateReglement && {
                key: 'new-payment',
                icon: <PlusOutlined />,
                label: 'Nouveau règlement',
                onClick: () => navigate('/dashboard/nouveau-reglement', { state: { presetClient: record.client } }),
            },
        ].filter(Boolean),
    });

    // ─── Colonnes ─────────────────────────────────────────────────────
    const columns = [
        {
            title: 'Client',
            key: 'client',
            width: 260,
            render: (_, record) => {
                const c = record.client;
                if (!c) return '—';
                const name = `${c.nom || ''} ${c.prenom || ''}`.trim();
                const isEntreprise = c.type === 'entreprise';
                return (
                    <div className="reglement-cell-client">
                        <div className={`reglement-cell-avatar ${isEntreprise ? 'is-entreprise' : 'is-particulier'}`}>
                            {isEntreprise ? <BankOutlined /> : <UserOutlined />}
                        </div>
                        <div className="reglement-cell-client-body">
                            <div className="reglement-cell-name">{name}</div>
                            <div className="reglement-cell-phone">
                                <PhoneOutlined /> {formatPhone(c.telephone)}
                            </div>
                            <Tag color={isEntreprise ? 'orange' : 'blue'} className="reglement-cell-type">
                                {isEntreprise ? 'Entreprise' : 'Particulier'}
                            </Tag>
                        </div>
                    </div>
                );
            },
        },
        {
            title: 'Contrats',
            dataIndex: 'nbContrats',
            key: 'nbContrats',
            width: 100,
            align: 'center',
            render: (val) => (
                <span className={`reglement-cell-nb ${val > 0 ? 'has-contrats' : ''}`}>
                    {val || 0}
                </span>
            ),
        },
        {
            title: 'Total primes',
            dataIndex: 'totalPrimeTTC',
            key: 'totalPrimeTTC',
            width: 140,
            align: 'right',
            sorter: (a, b) => (a.totalPrimeTTC || 0) - (b.totalPrimeTTC || 0),
            render: (val) => <span className="reglement-amount">{formatMontant(val || 0)}</span>,
            responsive: ['md'],
        },
        {
            title: 'Payé',
            dataIndex: 'totalVerse',
            key: 'totalVerse',
            width: 140,
            align: 'right',
            sorter: (a, b) => (a.totalVerse || 0) - (b.totalVerse || 0),
            render: (val) => <span className="reglement-amount is-positive">{formatMontant(val || 0)}</span>,
        },
        {
            title: 'Reste',
            dataIndex: 'resteAPayer',
            key: 'resteAPayer',
            width: 140,
            align: 'right',
            sorter: (a, b) => (a.resteAPayer || 0) - (b.resteAPayer || 0),
            render: (val) => {
                const v = Math.max(0, val);
                return (
                    <span className={`reglement-amount ${v > 0 ? 'is-warning' : 'is-positive'}`}>
                        {formatMontant(v)}
                    </span>
                );
            },
            responsive: ['md'],
        },
        {
            title: 'Progression',
            key: 'progress',
            width: 170,
            align: 'center',
            render: (_, record) => {
                const percent = Math.round(record.progressPaiement || 0);
                const tone = percent >= 100 ? 'paid' : percent > 0 ? 'partial' : 'unpaid';
                const color = percent >= 100 ? '#16a34a' : percent > 0 ? '#1A2B5F' : '#C8102E';
                return (
                    <div className={`reglement-progress reglement-progress-${tone}`}>
                        <Progress
                            percent={percent}
                            size="small"
                            strokeColor={color}
                            showInfo={false}
                            style={{ width: 120, margin: 0 }}
                        />
                        <div className="reglement-progress-label">
                            <strong>{percent}%</strong> {percent >= 100 ? 'payé' : percent > 0 ? 'partiel' : 'impayé'}
                        </div>
                    </div>
                );
            },
            responsive: ['md'],
        },
        {
            title: 'Dernier paiement',
            key: 'date',
            width: 130,
            render: (_, record) => {
                const d = record.lastReglementDate || record.lastCreatedAt;
                if (!d) return <span className="reglement-cell-date">—</span>;
                return <span className="reglement-cell-date">{new Date(d).toLocaleDateString('fr-FR')}</span>;
            },
            responsive: ['lg'],
        },
        {
            title: 'Moyens',
            key: 'moyens',
            width: 160,
            render: (_, record) => {
                const modes = record.moyensPaiement || [];
                if (modes.length === 0) return <span className="reglement-cell-date">—</span>;
                return (
                    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
                        {modes.map((m) => {
                            const cfg = MODE_PAIEMENT[m] || { label: m, color: 'default' };
                            return <Tag key={m} color={cfg.color} style={{ margin: 0 }}>{cfg.label}</Tag>;
                        })}
                    </span>
                );
            },
            responsive: ['md'],
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 160,
            align: 'center',
            fixed: 'right',
            render: (_, record) => (
                <div className="reglement-cell-actions">
                    <Button
                        type="primary"
                        size="small"
                        icon={<EyeOutlined />}
                        className="btn-detail reglement-action-detail"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/dashboard/detail-reglement/${record.client?._id}`);
                        }}
                    >
                        Détail
                    </Button>
                    <Dropdown menu={getRowActionsMenu(record)} trigger={['click']} placement="bottomRight">
                        <Button
                            type="text"
                            size="small"
                            icon={<MoreOutlined />}
                            onClick={(e) => e.stopPropagation()}
                            aria-label="Plus d'actions"
                            className="reglement-action-more"
                        />
                    </Dropdown>
                </div>
            ),
        },
    ];

    const tabItems = [
        { key: 'all',         label: <span><TeamOutlined /> Tous ({totalAll})</span> },
        { key: 'particulier', label: <span><UserOutlined /> Particuliers ({countByType.particulier?.count || 0})</span> },
        { key: 'entreprise',  label: <span><BankOutlined /> Entreprises ({countByType.entreprise?.count || 0})</span> },
    ];

    return (
        <div className="reglements-page">
            {/* Header */}
            <header className="reglements-header">
                <div className="reglements-header-text">
                    <h1 className="reglements-title">
                        <DollarOutlined /> Règlements
                    </h1>
                    <p className="reglements-subtitle">Paiements et versements</p>
                </div>
                <div className="reglements-header-actions">
                    <Button
                        icon={<QuestionCircleOutlined />}
                        onClick={() => setGuideOpen(true)}
                        style={{ borderRadius: 8, background: '#1A2B5F', borderColor: '#1A2B5F', color: '#fff', fontWeight: 600 }}
                    >
                        {!isMobile && 'Guide'}
                    </Button>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={() => fetchData()}
                        className="reglements-btn-refresh"
                        aria-label="Rafraîchir"
                    />
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => navigate('/dashboard/nouveau-reglement')}
                        className="reglements-btn-new"
                    >
                        {!isMobile && 'Nouveau règlement'}
                    </Button>
                </div>
            </header>

            <Modal
                open={guideOpen}
                onCancel={() => setGuideOpen(false)}
                title={<span><QuestionCircleOutlined style={{ color: '#1A2B5F', marginRight: 8 }} />Guide — Enregistrer un règlement</span>}
                closeIcon={(
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px',
                        background: '#C8102E', border: '1px solid #C8102E', borderRadius: 8,
                        fontSize: '0.9rem', fontWeight: 600, color: '#fff',
                    }}>
                        <CloseOutlined /> Fermer
                    </span>
                )}
                footer={<Button type="primary" onClick={() => setGuideOpen(false)}>J'ai compris</Button>}
                width={620}
            >
                <span style={{ color: '#8c8c8c' }}>
                    Un règlement enregistre un paiement reçu d'un client sur l'un de ses contrats.
                    Cliquez sur « Nouveau règlement », puis suivez ces étapes.
                </span>
                {(() => {
                    const liStyle = {
                        display: 'flex', gap: 12, padding: '14px 16px', marginBottom: 10,
                        background: '#f6f8fb', border: '1px solid #E8ECF1', borderRadius: 12, lineHeight: 1.5,
                    };
                    const icStyle = { flexShrink: 0, fontSize: 18, color: '#1A2B5F', marginTop: 2 };
                    const noteStyle = {
                        marginTop: 8, padding: '10px 12px', background: 'rgba(200,16,46,0.07)',
                        border: '1px solid rgba(200,16,46,0.25)', borderRadius: 8, fontSize: '0.9rem', lineHeight: 1.45,
                    };
                    return (
                        <div style={{ marginTop: 16 }}>
                            <div style={liStyle}>
                                <UserOutlined style={icStyle} />
                                <div>
                                    <strong>1 · Rechercher le client</strong> — Tapez le nom, le téléphone
                                    ou le code, puis sélectionnez le client dans la liste.
                                    <div style={noteStyle}>
                                        <strong style={{ color: '#C8102E' }}>Important :</strong> si le client
                                        n'apparaît pas, c'est qu'il n'a pas encore de contrat. Créez d'abord le
                                        client et son contrat avant de pouvoir enregistrer un règlement.
                                    </div>
                                </div>
                            </div>
                            <div style={liStyle}>
                                <FileProtectOutlined style={icStyle} />
                                <div>
                                    <strong>2 · Choisir le contrat</strong> — Si le client a plusieurs
                                    contrats, sélectionnez celui concerné. Le <strong>reste à payer</strong>
                                    s'affiche ; vous pouvez le reporter automatiquement dans le montant.
                                </div>
                            </div>
                            <div style={liStyle}>
                                <DollarOutlined style={icStyle} />
                                <div>
                                    <strong>3 · Montant</strong> — Saisissez le montant payé (en FCFA).
                                    Il peut être total ou partiel : la progression du contrat se met à jour
                                    automatiquement (ex. « 68% partiel »).
                                </div>
                            </div>
                            <div style={liStyle}>
                                <WalletOutlined style={icStyle} />
                                <div>
                                    <strong>4 · Mode de paiement</strong> — Espèces, chèque, virement,
                                    mobile money… Pour un chèque/virement, renseignez la
                                    <strong> Référence</strong> et la <strong>Banque</strong>.
                                </div>
                            </div>
                            <div style={liStyle}>
                                <CalendarOutlined style={icStyle} />
                                <div>
                                    <strong>5 · Date du règlement</strong> — Indiquez la date à laquelle
                                    le paiement a été reçu.
                                </div>
                            </div>
                            <div style={{ ...liStyle, marginBottom: 0 }}>
                                <CheckCircleOutlined style={icStyle} />
                                <div>
                                    <strong>6 · Enregistrer</strong> — Validez. Le paiement apparaît dans
                                    « Payé », le « Reste » diminue, et l'historique est consultable via le
                                    bouton <strong>« Détail »</strong> du client.
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </Modal>

            {/* KPIs */}
            <section className="reglements-kpis" aria-label="Indicateurs règlements">
                <MiniKpiCard
                    icon={<UserOutlined />}
                    label="Particuliers"
                    value={countByType.particulier?.count || 0}
                    suffix={countByType.particulier?.totalPrimes
                        ? `(${formatMontant(countByType.particulier.totalPrimes)})`
                        : ''}
                    tone="blue"
                />
                <MiniKpiCard
                    icon={<BankOutlined />}
                    label="Entreprises"
                    value={countByType.entreprise?.count || 0}
                    suffix={countByType.entreprise?.totalPrimes
                        ? `(${formatMontant(countByType.entreprise.totalPrimes)})`
                        : ''}
                    tone="orange"
                />
                <MiniKpiCard
                    icon={<CheckCircleOutlined />}
                    label="Total payé"
                    value={formatMontant(stats.totalVerse || 0)}
                    tone="green"
                />
                <MiniKpiCard
                    icon={<ExclamationCircleOutlined />}
                    label="En attente"
                    value={formatMontant(stats.totalResteAPayer || 0)}
                    tone="red"
                />
            </section>

            {/* Carte principale : toolbar + tabs + table */}
            <Card className="reglements-main-card">
                {/* Toolbar inline */}
                <div className="reglements-toolbar">
                    <Input
                        allowClear
                        placeholder="Rechercher par nom, téléphone..."
                        prefix={<SearchOutlined style={{ color: 'var(--cap-text-muted)' }} />}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="reglements-search"
                        aria-label="Rechercher un client"
                    />
                    {canSwitchAgence && (
                        <Select
                            value={filterAgence}
                            onChange={setFilterAgence}
                            className="reglements-filter"
                            aria-label="Filtrer par agence"
                        >
                            <Option value="all">Toutes les agences</Option>
                            {agences.map((a) => (
                                <Option key={a._id} value={a._id}>{a.nom}</Option>
                            ))}
                        </Select>
                    )}
                    <RangePicker
                        format="DD/MM/YYYY"
                        placeholder={['Date début', 'Date fin']}
                        onChange={setFilterDates}
                        value={filterDates}
                        className="reglements-filter-dates"
                        allowClear
                    />
                    {(activeFiltersCount > 0 || search) && (
                        <Button
                            icon={<ClearOutlined />}
                            onClick={clearAllFilters}
                            className="reglements-btn-clear"
                        >
                            Effacer
                        </Button>
                    )}
                    <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                        <Button icon={<DownloadOutlined />} className="reglements-btn-export">
                            Exporter
                        </Button>
                    </Dropdown>
                </div>

                {/* Tabs */}
                <Tabs
                    activeKey={activeTab}
                    onChange={(key) => { setActiveTab(key); setPagination((p) => ({ ...p, current: 1 })); }}
                    items={tabItems}
                    className="reglements-tabs"
                />

                {/* Liste */}
                {isMobile ? (
                    <Spin spinning={loading}>
                        {data.length === 0 ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucun client" style={{ padding: 32 }} />
                        ) : (
                            <List
                                dataSource={data}
                                rowKey={(r) => r.client?._id || r._id}
                                split={false}
                                renderItem={(record) => {
                                    const c = record.client || {};
                                    const isEntreprise = c.type === 'entreprise';
                                    const name = `${c.nom || ''} ${c.prenom || ''}`.trim();
                                    const reste = Math.max(0, record.resteAPayer || 0);
                                    const percent = Math.round(record.progressPaiement || 0);
                                    return (
                                        <Card
                                            key={record._id}
                                            size="small"
                                            className={`reglements-mobile-card ${isEntreprise ? 'is-entreprise' : 'is-particulier'}`}
                                            onClick={() => navigate(`/dashboard/detail-reglement/${c._id}`)}
                                        >
                                            <div className="mobile-card-row">
                                                <div className={`reglement-cell-avatar ${isEntreprise ? 'is-entreprise' : 'is-particulier'}`}>
                                                    {isEntreprise ? <BankOutlined /> : <UserOutlined />}
                                                </div>
                                                <div className="mobile-card-body">
                                                    <div className="mobile-card-name">{name || '—'}</div>
                                                    <div className="mobile-card-phone"><PhoneOutlined /> {formatPhone(c.telephone)}</div>
                                                    <div className="mobile-card-tags">
                                                        <Tag color={isEntreprise ? 'orange' : 'blue'}>
                                                            {isEntreprise ? 'Entreprise' : 'Particulier'}
                                                        </Tag>
                                                        <Tag>{record.nbContrats || 0} contrat{record.nbContrats > 1 ? 's' : ''}</Tag>
                                                    </div>
                                                    <div className="mobile-card-amounts">
                                                        <span>Payé : <strong className="reglement-amount is-positive">{formatMontant(record.totalVerse || 0)}</strong></span>
                                                        <span>Reste : <strong className={`reglement-amount ${reste > 0 ? 'is-warning' : 'is-positive'}`}>{formatMontant(reste)}</strong></span>
                                                    </div>
                                                    <Progress
                                                        percent={percent}
                                                        size="small"
                                                        strokeColor={percent >= 100 ? '#16a34a' : percent > 0 ? '#1A2B5F' : '#C8102E'}
                                                        format={(p) => `${p}%`}
                                                        style={{ marginTop: 6 }}
                                                    />
                                                </div>
                                                <Dropdown menu={getRowActionsMenu(record)} trigger={['click']}>
                                                    <Button
                                                        type="text"
                                                        icon={<MoreOutlined />}
                                                        onClick={(e) => e.stopPropagation()}
                                                        aria-label="Actions"
                                                    />
                                                </Dropdown>
                                            </div>
                                        </Card>
                                    );
                                }}
                            />
                        )}
                        <div className="reglements-pagination-wrapper">
                            <Pagination
                                current={pagination.current}
                                pageSize={pagination.pageSize}
                                total={pagination.total}
                                onChange={(p, ps) => fetchData(p, ps)}
                                size="small"
                                simple
                            />
                        </div>
                    </Spin>
                ) : (
                    <Table
                        dataSource={data}
                        columns={columns}
                        rowKey={(r) => r.client?._id || r._id}
                        loading={{ spinning: loading, size: 'large', tip: 'Chargement…' }}
                        pagination={{
                            ...pagination,
                            onChange: (p, ps) => fetchData(p, ps),
                            showSizeChanger: true,
                            showTotal: (t, range) => `${range[0]}-${range[1]} sur ${t} client${t > 1 ? 's' : ''}`,
                        }}
                        size="middle"
                        scroll={{ x: 'max-content' }}
                        onRow={(record) => ({
                            onClick: () => navigate(`/dashboard/detail-reglement/${record.client?._id}`),
                            style: { cursor: 'pointer' },
                        })}
                        locale={{
                            emptyText: loading ? (
                                <div style={{ minHeight: 220 }} />
                            ) : (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={
                                        activeFiltersCount > 0 || search
                                            ? 'Aucun client ne correspond aux filtres'
                                            : 'Aucun client'
                                    }
                                >
                                    {(activeFiltersCount > 0 || search) && (
                                        <Button icon={<ClearOutlined />} onClick={clearAllFilters}>
                                            Effacer les filtres
                                        </Button>
                                    )}
                                </Empty>
                            ),
                        }}
                    />
                )}
            </Card>

            <MobileFab
                icon={<PlusOutlined />}
                onClick={() => navigate('/dashboard/nouveau-reglement')}
                title="Nouveau règlement"
            />
        </div>
    );
};

export default Reglements;
